import axios from "axios";
import update from "lodash/fp/update";
import map from "lodash/fp/map";
import find from "lodash/fp/find";

const buildProduct = (products) => (product) => {
  if (!product?.parentId) {
    return product;
  }

  const baseProduct = find({ id: product.parentId })(products);

  return {
    ...baseProduct,
    ...product,
  };
};

// Function to load individual product files with graceful error handling
const loadProductFiles = async (products, basePath, cancelToken) => {
  if (
    Array.isArray(products) &&
    products.length > 0 &&
    typeof products[0] === "string"
  ) {
    const productPromises = products.map(async (filename) => {
      try {
        const productPath = `${basePath.replace(
          /\/[^/]+$/,
          ""
        )}/products/${filename}`;
        const response = await axios.get(productPath, { cancelToken });
        return response.data;
      } catch (error) {
        console.error(`Failed to load product file: ${filename}`);
        return null; // Return null for failed files
      }
    });

    const results = await Promise.all(productPromises);

    return results.filter((product) => product !== null);
  }

  // If products is already an array of objects, return as-is
  return products;
};

export default function fetchProductFormData({ signal }) {
  const source = axios.CancelToken.source();

  if (signal) {
    signal.addEventListener("abort", () => {
      source.cancel();
    });
  }

  return async ({ productFormDataUrl }) => {
    try {
      // Fetch the main form data
      const response = await axios.get(productFormDataUrl, {
        cancelToken: source.token,
      });
      const initialFormData = response.data;

      // Load product files if products are file references
      const loadedProducts = await loadProductFiles(
        initialFormData.products,
        productFormDataUrl,
        source.token
      );

      // Update the form data with loaded products and apply buildProduct logic
      return update(
        "products",
        map(buildProduct(loadedProducts))
      )({ ...initialFormData, products: loadedProducts });
    } catch (error) {
      throw error;
    }
  };
}
